import { format } from "date-fns";
import { Banner, Button, Modal } from "@components";
import { useActionInstalments } from "@api/actionsController";
import { useUsers } from "@api/usersController";
import { formatMoney, projectionIdentities } from "@areas/calendar/common";
import {
  cancellationBanner,
  cancellationKind,
  cancellationText,
} from "./cancellationCopy";

const parseDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatSendTime = (date) =>
  format(date, date.getMinutes() === 0 ? "haaa" : "h:mmaaa");

// The confirmation gate for cancelling scheduled work from a suggestion card. A card's Confirm
// opens this rather than cancelling outright, mirroring the calendar's delete confirmation: the
// real plan/promise card (fetched by its action id, exactly as the calendar's PlanDetailModal does)
// + a red consequences banner + Back / danger action. `onConfirm` is what actually cancels;
// `submitting` keeps the danger button spinning until the mutation settles.
export default function CancelActionConfirmModal({
  isOpen,
  params,
  onConfirm,
  onClose,
  submitting = false,
}) {
  const actionId = params?.targetActionId;
  const { data, isLoading } = useActionInstalments(actionId, isOpen);
  const { data: users } = useUsers();

  if (!params) return null;

  const kind = cancellationKind(params);
  // The branded tile the calendar's cancel confirmation uses — indigo plan / promise-teal promise — so
  // this modal reads as the same surface. Payment-request targets fall back to the promise look.
  const identity =
    projectionIdentities[params.targetKind] ?? projectionIdentities.PromiseToPay;
  const { Icon } = identity;

  // The plan/promise's own details, keyed by the action being cancelled — the same header the
  // calendar's PlanDetailModal renders (amount, cadence, instalment count, start), so the card
  // shows the actual work rather than a summary derived from the cancel suggestion.
  const header = data?.header;
  const customerRef = [header?.customerName, header?.reference && `(${header.reference})`]
    .filter(Boolean)
    .join(" ");
  const money = formatMoney(header?.amount);
  const start = parseDate(header?.startsOn);
  // A plan reads "Starting <date>"; a single promise-to-pay send just names the date.
  const isMultiInstalment = (header?.count ?? 0) > 1;
  const when = start
    ? `${isMultiInstalment ? "Starting " : ""}${format(start, "EEE, dd MMM yyyy")} at ${formatSendTime(start)}`
    : null;
  const summary = header
    ? [
        [money, header.cadence].filter(Boolean).join(" "),
        isMultiInstalment ? `${header.count} instalments` : null,
        when,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  const createdAt = parseDate(header?.createdAt);
  const createdBy = header?.createdByUserId
    ? users?.find(
        (u) =>
          String(u.id).toLowerCase() ===
          String(header.createdByUserId).toLowerCase(),
      )?.name
    : null;
  const createdLine = createdAt
    ? [`Created ${format(createdAt, "EEEE, dd MMMM yyyy 'at' HH:mm")}`, createdBy]
        .filter(Boolean)
        .join(" · ")
    : null;

  // Prefer the fetched plan summary; fall back to the suggestion-derived sentence while it loads
  // or if the action can't be read, so the card is never empty.
  const body = summary ?? (isLoading ? "Loading…" : cancellationText(params));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cancel ${kind}`}
      titleClassName="text-lg"
      size="md"
      preventBackdropClose={submitting}
    >
      <div className="flex flex-col">
        <div className="space-y-4 p-5">
          <div className="flex items-center gap-3.5 rounded-2xl border border-zinc-200 p-4">
            <div
              className={`flex size-10 shrink-0 items-center justify-center rounded-md ${identity.tile}`}
            >
              <Icon className="size-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              {customerRef && (
                <p className="text-sm font-semibold text-zinc-900">{customerRef}</p>
              )}
              <p className="text-sm text-zinc-600">{body}</p>
              {createdLine && (
                <p className="text-xs text-zinc-400">{createdLine}</p>
              )}
            </div>
          </div>

          <Banner
            tone="error"
            message={cancellationBanner(params)}
            textClassName="text-xs font-medium text-zinc-800"
            className="rounded-xl"
          />
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 px-5 py-4">
          <Button color="secondary" onClick={onClose} disabled={submitting}>
            Back
          </Button>
          <Button color="danger" onClick={onConfirm} submitting={submitting}>
            Cancel {kind}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
